import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import './NavigationDrawer.css';

/**
 * Props for NavigationDrawer component
 */
export interface NavigationDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Current credits balance */
  credits: number;
  /** Callback when drawer is closed */
  onClose: () => void;
  /** Callback when Buy Credits is tapped */
  onBuyCredits: () => void;
}

/**
 * NavigationDrawer Component
 * 
 * Slide-out navigation menu for mobile that appears from the right side.
 * Provides access to all main navigation pages and user account features.
 */
export const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  isOpen,
  credits,
  onClose,
  onBuyCredits,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, signIn, signOut } = useAuth();

  /**
   * Handle navigation to a page
   */
  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  /**
   * Handle sign in
   */
  const handleSignIn = () => {
    onClose();
    signIn();
  };

  /**
   * Handle sign out
   */
  const handleSignOut = () => {
    onClose();
    signOut();
  };

  /**
   * Handle buy credits
   */
  const handleBuyCredits = () => {
    onClose();
    onBuyCredits();
  };

  return (
    <>
      {/* Backdrop overlay */}
      {isOpen && (
        <div 
          className="nav-drawer-backdrop" 
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      
      {/* Drawer container */}
      <nav 
        className={`nav-drawer ${isOpen ? 'nav-drawer-open' : ''}`}
        role="navigation" 
        aria-label="Main navigation"
        aria-hidden={!isOpen}
      >
        {/* Header with close button */}
        <div className="nav-drawer-header">
          <div className="nav-drawer-logo">
            <i className="fa-solid fa-music"></i>
            <span>8-Bit Composer</span>
          </div>
          <button
            className="nav-drawer-close"
            onClick={onClose}
            aria-label="Close navigation menu"
            title="Close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* User info section (if authenticated) */}
        {isAuthenticated && user && (
          <div className="nav-drawer-user">
            <div className="nav-drawer-user-avatar">
              <i className="fa-solid fa-user"></i>
            </div>
            <div className="nav-drawer-user-info">
              <div className="nav-drawer-user-name">{user.email}</div>
              <div className="nav-drawer-user-status">Signed in</div>
            </div>
          </div>
        )}

        {/* Navigation links */}
        <div className="nav-drawer-content">
          {/* Credits Section - Always show, but behavior depends on auth */}
          <div className="nav-drawer-section">
            <button
              className="nav-drawer-item nav-drawer-item-credits"
              onClick={handleBuyCredits}
              aria-label={isAuthenticated ? "Buy more credits" : "Sign in to buy credits"}
            >
              <i className="fa-solid fa-coins"></i>
              <span>{isAuthenticated ? `Credits: ${credits}` : 'Buy Credits'}</span>
              <i className="fa-solid fa-plus nav-drawer-item-badge"></i>
            </button>
          </div>

          {/* Divider */}
          <div className="nav-drawer-divider"></div>

          {/* Main Pages */}
          <div className="nav-drawer-section">
            <button
              className="nav-drawer-item"
              onClick={() => handleNavigate('/')}
              aria-label="Go to Composer"
            >
              <i className="fa-solid fa-music"></i>
              <span>Composer</span>
            </button>

            {isAuthenticated ? (
              <>
                <button
                  className="nav-drawer-item"
                  onClick={() => handleNavigate('/my-compositions')}
                  aria-label="Go to My Compositions"
                >
                  <i className="fa-solid fa-folder-music"></i>
                  <span>My Compositions</span>
                </button>

                <button
                  className="nav-drawer-item"
                  onClick={() => handleNavigate('/profile')}
                  aria-label="Go to Profile"
                >
                  <i className="fa-solid fa-user"></i>
                  <span>Profile</span>
                </button>
              </>
            ) : (
              <button
                className="nav-drawer-item"
                onClick={handleSignIn}
                aria-label="Sign in"
              >
                <i className="fa-solid fa-right-to-bracket"></i>
                <span>Sign In</span>
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="nav-drawer-divider"></div>

          {/* Account & Info */}
          <div className="nav-drawer-section">
            <button
              className="nav-drawer-item"
              onClick={() => handleNavigate('/about')}
              aria-label="Go to About"
            >
              <i className="fa-solid fa-circle-info"></i>
              <span>About</span>
            </button>

            <button
              className="nav-drawer-item"
              onClick={() => handleNavigate('/faq')}
              aria-label="Go to FAQ"
            >
              <i className="fa-solid fa-circle-question"></i>
              <span>FAQ</span>
            </button>

            <button
              className="nav-drawer-item"
              onClick={() => handleNavigate('/contact')}
              aria-label="Go to Contact"
            >
              <i className="fa-solid fa-envelope"></i>
              <span>Contact</span>
            </button>
          </div>

          {/* Sign Out - Only show when authenticated */}
          {isAuthenticated && (
            <>
              <div className="nav-drawer-divider"></div>
              <div className="nav-drawer-section">
                <button
                  className="nav-drawer-item nav-drawer-item-signout"
                  onClick={handleSignOut}
                  aria-label="Sign out"
                >
                  <i className="fa-solid fa-right-from-bracket"></i>
                  <span>Sign Out</span>
                </button>
              </div>
            </>
          )}
        </div>
      </nav>
    </>
  );
};
